import React from 'react';
import { motion } from 'framer-motion';
import { User, Bot, Clock } from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  type?: 'text' | 'voice' | 'file';
}

interface ChatMessageProps {
  message: Message;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.sender === 'user';
  
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} group`}
    >
      <div className={`flex items-start space-x-4 max-w-xs md:max-w-md lg:max-w-lg ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
          isUser ? 'bg-gradient-to-br from-primary-500 to-primary-600' : 'bg-gradient-to-br from-modern-700 to-modern-800'
        }`}>
          {isUser ? (
            <User className="w-5 h-5 text-white" />
          ) : (
            <Bot className="w-5 h-5 text-white" />
          )}
        </div>

        {/* Message Bubble */}
        <div className={`relative ${isUser ? 'text-right' : 'text-left'}`}>
          <div className={`message-bubble ${
            isUser ? 'user-message' : 'ai-message'
          } shadow-xl`}>
            {isUser ? (
              <p className="text-sm leading-relaxed whitespace-pre-wrap font-medium">
                {message.text}
              </p>
            ) : (
              <div className="text-sm leading-relaxed">
                <MarkdownRenderer content={message.text} />
              </div>
            )}
          </div>
          
          {/* Message Type Indicator */}
          {message.type && (
            <div className={`absolute -top-2 ${isUser ? '-right-2' : '-left-2'} bg-modern-200 rounded-xl p-1 shadow-lg`}>
              {message.type === 'voice' && (
                <div className="w-3 h-3 bg-primary-500 rounded-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              )}
              {message.type === 'file' && (
                <div className="w-3 h-3 bg-primary-400 rounded-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              )}
            </div>
          )}
          
          {/* Timestamp */}
          <div className={`flex items-center space-x-1 mt-2 text-xs text-modern-400 font-medium ${
            isUser ? 'justify-end' : 'justify-start'
          }`}>
            <Clock className="w-3 h-3" />
            <span>{formatTime(message.timestamp)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ChatMessage; 